import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, ArrowLeft, Plus, Edit2, Trash } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import NotFound from "@/components/common/NotFound";
import { notify } from "@/components/common/Notification";
import SchemaFormView from "@/components/Schema/SchemaFormView";
import { createForm } from "@formily/core";
import { useQuery } from "@tanstack/react-query";
import { SchemaService, DynamicModuleService } from "@/services/APIs";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { checkFormChanges } from "@/lib/checkFormChanges";
import PTSNavigationRoutes from "../../utils/navigationRoutes";
import { Tabs, Modal, Form, Input, Popconfirm } from "antd";

const HOLIDAY_LIST_SCHEMA_NAME = "PTS Holiday List";
const CHILD_FIELD_FALLBACK = "pts_holiday_child";

/** Format date to DD-MM-YYYY for display and input */
function toDDMMYYYY(dateVal: string | Date | null | undefined): string {
  if (!dateVal) {
    return "";
  }
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) {
      return "";
    }
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return "";
  }
}

/** Format date to YYYY-MM-DD for native date input value */
function toYYYYMMDD(dateVal: string | Date | null | undefined): string {
  if (!dateVal) {
    return "";
  }
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) {
      return "";
    }
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

/** Parse YYYY-MM-DD (from date input) to ISO for API */
function fromYYYYMMDD(yyyyMmDd: string): string | null {
  if (!yyyyMmDd?.trim()) {
    return null;
  }
  const date = new Date(yyyyMmDd.trim());
  if (isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
}

type ChildItem = {
  index?: string | number;
  date?: string | null;
  description?: string | null;
  [key: string]: unknown;
};

const isWeekend = (item: ChildItem): boolean => {
  const d = (item.description ?? "").toString().trim().toLowerCase();
  return d === "saturday" || d === "sunday";
};

const generateTempId = () =>
  `temp_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const AddHolidayPage = () => {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = params?.id;
  const isEditMode = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("holiday");
  const [childItems, setChildItems] = useState<ChildItem[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ChildItem | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string | number>>(
    new Set()
  );
  const [modalForm] = Form.useForm<{ date: string; description: string }>();
  const form = useMemo(() => createForm({}), []);

  const { data: schemaInfo, isLoading: schemaLoading } = useQuery({
    queryKey: ["schema", HOLIDAY_LIST_SCHEMA_NAME],
    queryFn: () => SchemaService.getSchemaByName(HOLIDAY_LIST_SCHEMA_NAME),
    enabled: !!HOLIDAY_LIST_SCHEMA_NAME,
  });

  const { data: holidayInfo, isLoading: holidayLoading } = useQuery({
    queryKey: ["dynamic-model", HOLIDAY_LIST_SCHEMA_NAME, id],
    queryFn: () => {
      if (!id) {
        throw new Error("Holiday ID is required for edit mode");
      }
      return DynamicModuleService.getItemById(HOLIDAY_LIST_SCHEMA_NAME, id);
    },
    enabled: isEditMode && !!id,
  });

  const { childFieldName, schemaWithoutChildTable } = useMemo(() => {
    const raw = (schemaInfo?.json ?? schemaInfo) as
      | Record<string, unknown>
      | null
      | undefined;
    const schema = typeof raw === "object" && raw !== null ? raw : {};
    const innerSchema =
      schema && typeof schema === "object"
        ? ((schema as Record<string, unknown>).schema as
            | Record<string, unknown>
            | undefined)
        : undefined;
    const props =
      innerSchema?.properties ??
      (schema as Record<string, unknown>)?.properties ??
      {};
    const propObj =
      typeof props === "object" && props !== null
        ? (props as Record<string, unknown>)
        : {};
    let foundChildKey: string = CHILD_FIELD_FALLBACK;
    for (const [key, val] of Object.entries(propObj)) {
      const v = val as Record<string, unknown>;
      if (
        v?.["x-component"] === "CustomChildTable" ||
        (v?.["x-component-props"] &&
          (v["x-component-props"] as Record<string, unknown>)?.x_child)
      ) {
        foundChildKey = key;
        break;
      }
    }
    const nextProps = { ...propObj };
    delete nextProps[foundChildKey];
    let schemaWithoutChild: Record<string, unknown>;
    if (innerSchema) {
      schemaWithoutChild = {
        ...(typeof raw === "object" && raw !== null ? raw : {}),
        schema: { ...innerSchema, properties: nextProps },
      };
    } else {
      schemaWithoutChild = {
        ...(typeof raw === "object" && raw !== null ? raw : {}),
        ...(schema as Record<string, unknown>),
        properties: nextProps,
      };
    }
    return {
      childFieldName: foundChildKey,
      schemaWithoutChildTable: schemaWithoutChild,
    };
  }, [schemaInfo]);

  useEffect(() => {
    if (isEditMode && holidayInfo) {
      const cleaned = removeMetaFields(holidayInfo);
      form.setInitialValues(cleaned);
      const arr = cleaned[childFieldName] ?? cleaned.pts_holiday_child ?? [];
      setChildItems(
        Array.isArray(arr)
          ? arr.map((item: ChildItem) => ({
              ...item,
              index: (item.index ?? item.id ?? generateTempId()) as
                | string
                | number,
            }))
          : []
      );
    }
  }, [isEditMode, holidayInfo, form, childFieldName]);

  const holidayItems = useMemo(
    () => childItems.filter(item => !isWeekend(item)),
    [childItems]
  );
  const weekendItems = useMemo(
    () => childItems.filter(item => isWeekend(item)),
    [childItems]
  );

  const formatDateDisplay = useCallback(
    (dateVal: string | null | undefined): string => {
      const s = toDDMMYYYY(dateVal);
      return s || "--";
    },
    []
  );

  const openAdd = useCallback(() => {
    setEditingItem(null);
    modalForm.setFieldsValue({ date: "", description: "" });
    setModalOpen(true);
  }, [modalForm]);

  const openEdit = useCallback(
    (item: ChildItem) => {
      setEditingItem(item);
      modalForm.setFieldsValue({
        date: toYYYYMMDD(item.date),
        description: (item.description ?? "") as string,
      });
      setModalOpen(true);
    },
    [modalForm]
  );

  const handleModalOk = useCallback(() => {
    modalForm
      .validateFields()
      .then(values => {
        const dateVal = values.date ? fromYYYYMMDD(values.date) : null;
        const newItem: ChildItem = {
          date: dateVal,
          description: values.description?.trim() || null,
          index: editingItem?.index ?? generateTempId(),
        };
        if (editingItem !== null) {
          setChildItems(prev =>
            prev.map(it =>
              it === editingItem ? { ...it, ...newItem, index: it.index } : it
            )
          );
        } else {
          setChildItems(prev => [...prev, newItem]);
        }
        setModalOpen(false);
        setEditingItem(null);
        modalForm.resetFields();
      })
      .catch(() => {});
  }, [editingItem, modalForm]);

  const handleDelete = useCallback((item: ChildItem) => {
    // Match by reference only. Filtering by `it.index !== item.index` removes every
    // row that shares the same index (common when API omits id/index or sends duplicates).
    setChildItems(prev => prev.filter(it => it !== item));
  }, []);

  const handleSubmit = async () => {
    try {
      // Formily form exposes setFieldValue; ensure child data is on form before submit
      const formWithSet = form as unknown as {
        setFieldValue: (path: string, value: unknown) => void;
      };
      if (typeof formWithSet.setFieldValue === "function") {
        formWithSet.setFieldValue(childFieldName, childItems);
      } else {
        (
          form as unknown as { setValues: (v: Record<string, unknown>) => void }
        ).setValues({ [childFieldName]: childItems });
      }
      await form.validate();
      const modifiedValues = removeMetaFields(form.values);
      setIsSubmitting(true);

      if (isEditMode && id) {
        if (!checkFormChanges(modifiedValues, holidayInfo, notify)) {
          setIsSubmitting(false);
          return;
        }
        const response = await DynamicModuleService.updateItem(
          HOLIDAY_LIST_SCHEMA_NAME,
          id,
          modifiedValues
        );
        if (response) {
          navigate(PTSNavigationRoutes.HOLIDAY_LIST);
        }
      } else {
        const response = await DynamicModuleService.createItem(
          HOLIDAY_LIST_SCHEMA_NAME,
          modifiedValues
        );
        if (response) {
          navigate(PTSNavigationRoutes.HOLIDAY_LIST);
        }
      }
    } catch (error: unknown) {
      const msg =
        error instanceof Error
          ? error.message
          : `Failed to ${isEditMode ? "update" : "create"} holiday`;
      notify.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => navigate(PTSNavigationRoutes.HOLIDAY_LIST);

  const renderTable = (items: ChildItem[], tabType: "holiday" | "weekend") => (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">
        <span className="text-sm font-semibold text-gray-800">
          {tabType === "holiday" ? "Holidays" : "Weekends"}
        </span>
        <Button
          type="button"
          size="sm"
          onClick={openAdd}
          className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
        >
          <Plus className="h-4 w-4" />
          Add New
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="border-b border-gray-200 bg-gray-200 [&>th]:text-gray-800">
            <TableHead className="w-12 border-r border-gray-300 px-3 py-3 font-semibold">
              <input
                type="checkbox"
                checked={
                  items.length > 0 &&
                  items.every(i => selectedRowKeys.has(i.index ?? ""))
                }
                onChange={e => {
                  if (e.target.checked) {
                    const keys = items
                      .map(i => i.index)
                      .filter(
                        (k): k is string | number =>
                          k !== undefined && k !== null
                      );
                    setSelectedRowKeys(prev => new Set([...prev, ...keys]));
                  } else {
                    setSelectedRowKeys(prev => {
                      const s = new Set(prev);
                      items.forEach(i => {
                        if (i.index !== null) {
                          s.delete(i.index!);
                        }
                      });
                      return s;
                    });
                  }
                }}
              />
            </TableHead>
            <TableHead className="border-r border-gray-300 px-4 py-3 font-semibold">
              Date
            </TableHead>
            <TableHead className="border-r border-gray-300 px-4 py-3 font-semibold">
              Description
            </TableHead>
            <TableHead className="px-4 py-3 text-right font-semibold">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={4}
                className="py-10 text-center text-gray-500"
              >
                No data
              </TableCell>
            </TableRow>
          ) : (
            items.map((item, idx) => {
              const key = item.index ?? item.date ?? Math.random();
              return (
                <TableRow
                  key={String(key)}
                  className={`border-b border-gray-200 ${idx % 2 === 1 ? "bg-gray-50/50" : "bg-white"} hover:bg-gray-100`}
                >
                  <TableCell className="w-12 border-r border-gray-200 px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={
                        item.index !== null && selectedRowKeys.has(item.index!)
                      }
                      onChange={e => {
                        if (item.index === null) {
                          return;
                        }
                        if (e.target.checked) {
                          setSelectedRowKeys(
                            prev => new Set([...prev, item.index!])
                          );
                        } else {
                          setSelectedRowKeys(prev => {
                            const s = new Set(prev);
                            s.delete(item.index!);
                            return s;
                          });
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell className="border-r border-gray-200 px-4 py-2.5 text-sm">
                    {formatDateDisplay(item.date as string)}
                  </TableCell>
                  <TableCell className="border-r border-gray-200 px-4 py-2.5 text-sm">
                    {(item.description ?? "--") as string}
                  </TableCell>
                  <TableCell className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-4">
                      <button
                        type="button"
                        onClick={() => openEdit(item)}
                        className="text-primary hover:opacity-80"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <Popconfirm
                        title="Are you sure you want to delete this item?"
                        onConfirm={() => handleDelete(item)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-700"
                          title="Delete"
                        >
                          <Trash className="h-4 w-4" />
                        </button>
                      </Popconfirm>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );

  if (schemaLoading || (isEditMode && holidayLoading)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isEditMode && !holidayInfo) {
    return <NotFound entityName="Holiday" />;
  }

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#111827]">
              Add Holiday Data:
            </h1>
            <p className="text-sm text-[#627384]">
              {isEditMode
                ? "Update holiday and weekend list"
                : "Add holiday and weekend list"}
            </p>
          </div>
        </div>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
        >
          <Save className="h-4 w-4" />
          {isSubmitting ? "Saving..." : isEditMode ? "Update" : "Save"}
        </Button>
      </div>

      {/* Parent form: Holiday List Name, Total Holidays, From/To Date, Weekly Off, Country */}
      {schemaWithoutChildTable &&
        (() => {
          const s = schemaWithoutChildTable as Record<string, unknown>;
          const props =
            (s?.schema as Record<string, unknown>)?.properties ??
            s?.properties ??
            {};
          return (
            Object.keys(
              typeof props === "object" && props !== null ? props : {}
            ).length > 0
          );
        })() && (
          <Card className="border border-gray-200 bg-white">
            <CardContent className="p-6">
              <SchemaFormView
                form={form}
                schemaJson={schemaWithoutChildTable}
              />
            </CardContent>
          </Card>
        )}

      {/* Child list: Add Holiday Data with two tabs */}
      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-6">
          <h2 className="mb-4 text-lg font-semibold text-[#111827]">
            Add Holiday Data:
          </h2>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: "holiday",
                label: "Holiday List",
                children: renderTable(holidayItems, "holiday"),
              },
              {
                key: "weekend",
                label: "Weekend List",
                children: renderTable(weekendItems, "weekend"),
              },
            ]}
          />
        </CardContent>
      </Card>

      <Modal
        title={editingItem ? "Edit Item" : "Add New Item"}
        open={modalOpen}
        onOk={handleModalOk}
        onCancel={() => {
          setModalOpen(false);
          setEditingItem(null);
          modalForm.resetFields();
        }}
        okText={editingItem ? "Update" : "Save"}
        cancelText="Cancel"
        destroyOnClose
      >
        <Form form={modalForm} layout="vertical" className="mt-4">
          <Form.Item
            name="date"
            label="Date"
            rules={[{ required: true, message: "Please select date" }]}
          >
            <input
              type="date"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please enter description" }]}
          >
            <Input placeholder="e.g. Makar Sankranti, Saturday" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AddHolidayPage;
